import Swal from "sweetalert2";
import {Naja} from "naja";
import IExtension from "./IExtension";

/**
 * Adds confirmation to submitted form, if marked input was changed
 * @example ```html
 *    <input data-confirm-input-change="Are you sure?" data-confirm-input-change-yes="Do it!" data-confirm-input-change-no="Cancel">
 * ```
 * Add `data-confirm-input-change-ignore` attribute to the Submit button to skip the confirmation.
 */
export default class ConfirmInputChangeExtension implements IExtension {

    private confirmedElements: Set<Element> = new Set();

    initialize(naja: Naja) {
        naja.addEventListener('init', () => this.nonAjaxInteraction($('body')));
        naja.snippetHandler.addEventListener('afterUpdate', e => this.nonAjaxInteraction($(<HTMLElement>e.detail.snippet)));

        naja.uiHandler.addEventListener('interaction', e=> this.ajaxInteraction(e, e.detail.element));
    }

    private nonAjaxInteraction($root: JQuery): void {
        const $forms = $root.find('form:not(.ajax)');

        $forms.find('[data-confirm-input-change]').each((i: number, input: HTMLInputElement) => {
            const form = $(input).closest('form')[0];

            form.addEventListener('submit', e => {
                if (
                    this.isConfirmationIgnored(e.submitter)
                    || this.isElementConfirmed(input)
                    || !window.Nette.validateForm(form, true)
                ) {
                    return;
                }

                if (input.value !== input.defaultValue) {
                    e.preventDefault();

                    this.confirm(input).then((confirmed) => {
                        if (confirmed) {
                            this.markElementConfirmed(input);
                            e.submitter.click();
                        }
                    });
                }
            });
        });
    }

    private ajaxInteraction(e: any, element: Element): void {
        const $element = $(element);
        if ($element.is('input[type="submit"]')) {
            const $form = $element.closest('form');
            if (
                this.isConfirmationIgnored(element)
                || $form.length === 0
                || !window.Nette.validateForm($form[0], true)
            ) {
                return;
            }

            const $inputToConfirm = $form.find('input[data-confirm-input-change]').first();
            if ($inputToConfirm.length === 0) {
                return;
            }

            const input = <HTMLInputElement>$inputToConfirm[0];
            if (this.isElementConfirmed(input)) {
                this.removeConfirmedElement(input);
                return;
            }

            if (input.value !== input.defaultValue) {
                e.preventDefault();
                e.stopPropagation();
                e.stopImmediatePropagation();

                this.confirm(input).then((confirmed) => {
                    element.removeAttribute('disabled');
                    if (confirmed) {
                        this.markElementConfirmed(input);
                        element.click();
                    }
                });
            }
        }
    }

    private async confirm(input: HTMLElement): Promise<boolean> {
        const result = await Swal.fire({
            text: $(input).data('confirm-input-change'),
            showCancelButton: true,
            confirmButtonColor: "#8F3985",
            cancelButtonColor: "#6c757d",
            cancelButtonText: $(input).data('confirm-input-change-no'),
            confirmButtonText: $(input).data('confirm-input-change-yes'),
        });

        return result.isConfirmed === true;
    }

    private markElementConfirmed(element: Element): void {
        this.confirmedElements.add(element);
    }

    private isElementConfirmed(element: Element): boolean {
        return this.confirmedElements.has(element);
    }

    private removeConfirmedElement(element: Element): void {
        this.confirmedElements.delete(element);
    }

    private isConfirmationIgnored(submitter: Element): boolean {
        return submitter.hasAttribute('data-confirm-input-change-ignore');
    }
}
